import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import FlatLayout from 'components/Layout/FlatLayout';
import PageDescription from 'components/PageDescription';

import { sendSupixEvent } from 'lib/supix';

import { useUtm } from 'providers/UtmProvider';

import Breadcrumb from './Breadcrumb';
import Head from './Head';
import Schema from './Schema';
import Sidebar from './Sidebar';
import StoreSummary from './StoreSummary';
import TimelineList from './TimelineList';

const StoreAd = dynamic(() => import('./StoreAd'));

/**
 * We used to a specific field for it that came from the API. These titles
 * were being repeated for both offers and coupons pages from the same store.
 * We replaced it with the fn below so it doesn't causes SEO issues.
 */
const toMetaTitle = ({ isOfferPage, storeName }) =>
  isOfferPage
    ? `As melhores ofertas da ${storeName} - É no Promobit`
    : `Cupons de desconto ${storeName} - É no Promobit`;

const Store = ({
  gaEventCategory = 'offers_store',
  isExclusivelyCoupon = false,
  isStoreChecklistActive = true,
  popularStores = [],
  serverCoupons,
  serverOffers,
  store: {
    couponPageDescription,
    couponsMetaDescription,
    googleIndex,
    hasPhysicalStore,
    isStoreCnpjValid,
    isStoreVerified,
    offerPageDescription,
    offersMetaDescription,
    storeAddress,
    storeContacts,
    storeDescription,
    storeDomain,
    storeDomainEnriched,
    storeFacebookPage,
    storeId,
    storeImage,
    storeName,
    storeRating,
    storeRatingCount,
    storeSlug,
  },
  storeAd,
  fixedOffer = null,
}) => {
  const { isUtmLoaded } = useUtm();

  const isOfferPage = !!serverOffers;

  useEffect(() => {
    if (isUtmLoaded) {
      const endpoint = isOfferPage ? '/st' : '/cp';

      sendSupixEvent(endpoint, {
        id: storeId,
      });
    }
  }, [storeId, isOfferPage, isUtmLoaded]);

  const isStoreAdVisible = !!storeAd?.image;
  const pageDescription = isOfferPage
    ? offerPageDescription
    : couponPageDescription;

  return (
    <>
      <FlatLayout.Margin className="gap-8 lg:gap-12">
        <FlatLayout.Heading>
          <Head
            googleIndex={googleIndex}
            metaDescription={
              isOfferPage ? offersMetaDescription : couponsMetaDescription
            }
            metaTitle={toMetaTitle({ isOfferPage, storeName })}
          />
          <Schema
            isOfferPage={isOfferPage}
            storeName={storeName}
            storeSlug={storeSlug}
          />
          <Breadcrumb isOfferPage={isOfferPage} storeName={storeName} />
          <StoreSummary
            isExclusivelyCoupon={isExclusivelyCoupon}
            isStoreVerified={isStoreVerified}
            storeDomain={storeDomain}
            storeImage={storeImage}
            storeName={storeName}
          />
        </FlatLayout.Heading>
        <FlatLayout.Body>
          {isStoreAdVisible && (
            <StoreAd
              className="mb-6"
              gaEventCategory={gaEventCategory}
              storeName={storeName}
              {...storeAd}
            />
          )}
          <TimelineList
            fixedOffer={fixedOffer}
            gaEventCategory={gaEventCategory}
            isExclusivelyCoupon={isExclusivelyCoupon}
            isOfferPage={isOfferPage}
            serverCoupons={serverCoupons}
            serverOffers={serverOffers}
            storeId={storeId}
            storeName={storeName}
            storeSlug={storeSlug}
          />
          {pageDescription && (
            <PageDescription
              className="mt-18"
              pageDescription={pageDescription}
            />
          )}
        </FlatLayout.Body>
        <Sidebar
          gaEventCategory={gaEventCategory}
          hasPhysicalStore={hasPhysicalStore}
          isCnpjValid={isStoreCnpjValid}
          isStoreChecklistActive={isStoreChecklistActive}
          isVerified={isStoreVerified}
          popularStores={popularStores}
          storeAddress={storeAddress}
          storeContacts={storeContacts}
          storeDescription={storeDescription}
          storeDomain={storeDomain}
          storeDomainEnriched={storeDomainEnriched}
          storeFacebookPage={storeFacebookPage}
          storeName={storeName}
          storeRating={storeRating}
          storeRatingCount={storeRatingCount}
          storeSlug={storeSlug}
        />
      </FlatLayout.Margin>
      <FlatLayout.Footer />
    </>
  );
};

Store.propTypes = {
  fixedOffer: PropTypes.object,
  gaEventCategory: PropTypes.string,
  isExclusivelyCoupon: PropTypes.bool,
  isStoreChecklistActive: PropTypes.bool,
  popularStores: PropTypes.arrayOf(
    PropTypes.shape({
      storeName: PropTypes.string,
      storeSlug: PropTypes.string,
    })
  ),
  serverCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponVerified: PropTypes.bool,
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        couponPublished: PropTypes.string.isRequired,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ).isRequired,
  }),
  serverOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }),
  store: PropTypes.shape({
    storeId: PropTypes.number.isRequired,
    storeName: PropTypes.string.isRequired,
    couponPageDescription: PropTypes.string,
    offerPageDescription: PropTypes.string,
    storeDescription: PropTypes.string,
    pageCouponsTitle: PropTypes.string.isRequired,
    couponsMetaTitle: PropTypes.string.isRequired,
    couponsMetaDescription: PropTypes.string.isRequired,
    pageOffersTitle: PropTypes.string.isRequired,
    offersMetaDescription: PropTypes.string.isRequired,
    storeImage: PropTypes.string,
    storeSlug: PropTypes.string.isRequired,
    storeDomain: PropTypes.string.isRequired,
    storeDomainEnriched: PropTypes.string,
    storeRating: PropTypes.number,
    storeRatingCount: PropTypes.number,
    storeContacts: PropTypes.string,
    storeAddress: PropTypes.string,
    storeFacebookPage: PropTypes.string,
    googleIndex: PropTypes.bool,
    isStoreIndexed: PropTypes.bool.isRequired,
    isStoreVerified: PropTypes.bool.isRequired,
    isStoreCnpjValid: PropTypes.bool.isRequired,
    hasPhysicalStore: PropTypes.bool.isRequired,
  }).isRequired,
  storeAd: PropTypes.shape({
    description: PropTypes.string.isRequired,
    gaEventCategory: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    storeName: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
};

export default Store;
